import { type NotificationDeliveryStatus, type NotificationFailureReason } from '@aysar/contracts';

import { type BadgeTone } from '@/shared/components/ui/Badge/Badge.tokens';

/**
 * Who has to fix this failure.
 *
 * The delivery log's whole value is that the reason is a **coded** column rather
 * than the vendor's prose: `template_missing` is an operator who has not written
 * copy, `push_unavailable` is a broken integration, and the two lead to opposite
 * investigations. Grouping by the reason only helps if the grouping means
 * something, and this is what it means.
 *
 * `device` is the third answer and it is the one that is usually **nobody's
 * job**: a member who never granted permission, whose token FCM has retired, or
 * whose mail server refuses the address is not an incident. Folding it into
 * either of the other two would put a queue of non-problems in front of the
 * operator chasing a real one.
 */
export type FailureInvestigation = 'copy' | 'integration' | 'device' | 'unknown';

const INVESTIGATIONS: Readonly<Record<NotificationFailureReason, FailureInvestigation>> = {
  template_missing: 'copy',
  template_inactive: 'copy',
  template_render_failed: 'copy',
  push_unavailable: 'integration',
  no_device_token: 'device',
  push_rejected: 'device',
  email_unconfigured: 'integration',
  email_unavailable: 'integration',
  email_rejected: 'device',
  sms_unconfigured: 'integration',
  sms_unavailable: 'integration',
  sms_rejected: 'device',
};

/**
 * `unknown` is for an api ahead of this build, and it is never thrown: a reason
 * this client has not heard of still has to render as a row somebody can read,
 * with the coded value itself standing in for the label.
 */
export function failureInvestigation(reason: NotificationFailureReason): FailureInvestigation {
  return INVESTIGATIONS[reason] ?? 'unknown';
}

const STATUS_TONES: Readonly<Record<NotificationDeliveryStatus, BadgeTone>> = {
  // Terminal, and the only one that is evidence of something broken: a notice
  // nobody received and nothing will retry.
  dead: 'danger',
  // Still moving on its own — loud enough to notice, not loud enough to act on.
  failed: 'warning',
  sent: 'success',
  queued: 'neutral',
  sending: 'neutral',
};

export function deliveryStatusTone(status: NotificationDeliveryStatus): BadgeTone {
  return STATUS_TONES[status] ?? 'neutral';
}
